import { Problem } from "@cloudserver/contracts";
import type { z } from "zod";

export const API_BASE = "/api/v1";

/** An error answer of the API, with its problem details when it sent them. */
export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
  ) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = "ApiProblem";
  }

  get code(): string | null {
    return this.problem?.code ?? null;
  }
}

interface RequestOptions<S extends z.ZodType> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Validates the response; omit for empty (204) answers. */
  schema?: S;
  /** Sent with creates, so a retried request does not create twice (SPEC §7.1). */
  idempotencyKey?: string;
}

/**
 * Calls the CloudServer API with the session cookie. Responses are validated
 * against the shared contracts, so a server/client mismatch fails loudly.
 */
export async function api<S extends z.ZodType>(
  path: string,
  { method = "GET", body, schema, idempotencyKey }: RequestOptions<S> = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (idempotencyKey) headers["idempotency-key"] = idempotencyKey;
  const response = await fetch(API_BASE + path, {
    method,
    credentials: "same-origin",
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const parsed = Problem.safeParse(await response.json().catch(() => null));
    throw new ApiProblem(response.status, parsed.success ? parsed.data : null);
  }
  if (!schema || response.status === 204) return undefined as z.infer<S>;
  return schema.parse(await response.json());
}
